import { computed, type Ref } from 'vue'
import type { HabilitationDTO } from '@/features/api/habilitation/dto/habilitation'
import type { IconName } from '@designSystem/fondation/icons/iconsList'
import { usePaginatedListState } from '../usePaginatedListState'

export type ModeHabilitation = 'entite' | 'contact'

export type HabilitationVM = {
  id: string
  isMember: boolean
  siretLabel: string
  display: {
    icon: IconName
    title: string
    subtitleMain: string | null
    linkRoute: ModeHabilitation
    linkParam?: string
  }
  raw: HabilitationDTO
}

export function useHabilitationsAdminListState(opts: {
  habilitations: Ref<HabilitationDTO[] | undefined>
  mode: Ref<ModeHabilitation>
}) {
  const { habilitations, mode } = opts

  const toHabilitationVM = (e: HabilitationDTO): HabilitationVM => {
    const isMember = e.autorisations?.statut !== 'Visiteur'
    const hasNIC = !!e.entite.nic
    const siretLabel = hasNIC ? `Siret : ${e.entite.siren} ${e.entite.nic}` : `Siren : ${e.entite.siren}`

    const isEntite = mode.value === 'entite'
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    const display = isEntite
      ? {
          icon: 'profile' as IconName,
          title: emailRegex.test(e.contact?.username ?? e.userId ?? '')
            ? (e.contact?.username ?? e.userId ?? 'Utilisateur inconnu')
            : (e.contact?.email ?? 'Utilisateur inconnu'),
          subtitleMain: e.contact ? e.contact.email : null,
          linkParam: e.contact?.email,
          linkRoute: 'contact' as ModeHabilitation,
        }
      : {
          icon: 'entreprise' as IconName,
          title: e.entite.denomination,
          subtitleMain: null,
          linkParam: e.entite.nic ? `${e.entite.siren}${e.entite.nic}` : e.entite.siren,
          linkRoute: 'entite' as ModeHabilitation,
        }

    return {
      id: e.id?.toString() ?? `${e.userId}-${e.entite.siren}-${e.entite.nic ?? ''}`,
      isMember,
      siretLabel,
      display,
      raw: e,
    }
  }

  const { currentPage, nbPages, nbPagesMax, vmList, pageVMs, DROPDOWN_PERMS, PERM_TOGGLES } = usePaginatedListState(
    habilitations,
    toHabilitationVM,
  )

  const headerFirstCol = computed(() => (mode.value === 'entite' ? 'Utilisateur' : 'Comptes'))

  return { currentPage, nbPages, nbPagesMax, vmList, pageVMs, headerFirstCol, DROPDOWN_PERMS, PERM_TOGGLES }
}
